import { lastValueFrom } from 'rxjs';
import { ClientListService } from '../services/client/client-list.service';

// ============================================================================
// Interfaces
// ============================================================================

export interface ClientOption {
  id: string | number;
  value: string | number;
  label: string;
  client_code?: string;
  client_name?: string;
  [key: string]: unknown;
}

export interface ClientAddress {
  value: string | number;
  label: string;
  gst_number?: string;
  [key: string]: unknown;
}

export interface ClientAddresses {
  shipping: ClientAddress[];
  billing: ClientAddress[];
}

export interface ClientTypeaheadResult {
  options: ClientOption[];
  addresses: Map<string, ClientAddresses>;
  selectedClient?: ClientOption;
  error?: string;
}

export interface ClientSearchConfig {
  type?: 'non-draft' | 'draft' | 'all';
  limit?: number;
  searchField?: 'client_name' | 'phone' | 'category' | 'query';
  includeDeleted?: boolean;
  includeCancelled?: boolean;
  debounceMs?: number;
}

interface ClientApiResponse {
  id: string | number;
  client_name?: string;
  client_code?: string;
  client_shipping_address?: {
    id: string | number;
    shipping_address?: string;
    [key: string]: unknown;
  };
  client_tax_info?: Array<{
    id: string | number;
    address?: string;
    gst_number?: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_CONFIG: Required<ClientSearchConfig> = {
  type: 'non-draft',
  limit: 50,
  searchField: 'query',
  includeDeleted: false,
  includeCancelled: false,
  debounceMs: 300,
};

// ============================================================================
// Client Typeahead Utility Class
// ============================================================================

export class ClientTypeaheadUtils {
  
  /**
   * Normalize client ID from various formats (object, string, number)
   */
  static normalizeClientId(value: unknown): string | number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'object' && value !== null) {
      const obj = value as Record<string, unknown>;
      
      if (obj['value'] !== undefined && obj['value'] !== null && obj['value'] !== '') {
        return obj['value'] as string | number;
      }
      
      if (obj['id'] !== undefined && obj['id'] !== null && obj['id'] !== '') {
        return obj['id'] as string | number;
      }

      if (obj['client_id'] !== undefined && obj['client_id'] !== null && obj['client_id'] !== '') {
        return obj['client_id'] as string | number;
      }
      
      return null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    return null;
  }

  /**
   * Compare two client IDs for equality
   */
  static areClientIdsEqual(
    id1: string | number | null | undefined,
    id2: string | number | null | undefined
  ): boolean {
    if (id1 === null || id1 === undefined || id2 === null || id2 === undefined) {
      return false;
    }
    return String(id1) === String(id2);
  }

  /**
   * Format a single client into autocomplete option format
   */
  static formatClientOption(client: ClientApiResponse): ClientOption {
    const { id, client_name, client_code, ...rest } = client;
    let label: string;
    if (client_code && client_name) {
      label = `${client_code} - ${client_name}`;
    } else if (client_code) {
      label = client_code;
    } else if (client_name) {
      label = client_name;
    } else {
      label = `Client ${id}`;
    }
    
    return {
      ...rest,
      id,
      value: id,
      label,
      client_code,
      client_name,
    };
  }

  /**
   * Format multiple clients into autocomplete options
   */
  static formatClientOptions(clients: ClientApiResponse[]): ClientOption[] {
    return clients.map(client => this.formatClientOption(client));
  }

  /**
   * Extract shipping and billing addresses from client data
   */
  static extractClientAddresses(client: ClientApiResponse): ClientAddresses {
    const shippingAddresses: ClientAddress[] = [];
    const billingAddresses: ClientAddress[] = [];

    // Extract shipping address
    const shipping = client?.client_shipping_address;
    if (shipping?.id && shipping?.shipping_address) {
      shippingAddresses.push({
        value: shipping.id,
        label: shipping.shipping_address,
        ...shipping,
      });
    }

    // Extract billing addresses from tax_info
    if (Array.isArray(client.client_tax_info)) {
      for (const taxInfo of client.client_tax_info) {
        if (taxInfo?.id && taxInfo?.address) {
          billingAddresses.push({
            value: taxInfo.id,
            label: taxInfo.address,
            gst_number: taxInfo.gst_number,
            ...taxInfo,
          });
        }
      }
    }

    return { shipping: shippingAddresses, billing: billingAddresses };
  }

  /**
   * Search clients and return formatted result
   */
  static async searchClients(
    query: string,
    clientListService: ClientListService,
    currentClientId?: string | number | null,
    existingOptions: ClientOption[] = [],
    config: ClientSearchConfig | number = {}
  ): Promise<ClientTypeaheadResult> {
    const resolvedConfig: ClientSearchConfig = typeof config === 'number' 
      ? { limit: config } 
      : config;
    const mergedConfig = { ...DEFAULT_CONFIG, ...resolvedConfig };
    const normalizedQuery = (query || '').trim();

    if (!normalizedQuery) {
      return this.createEmptyResult(currentClientId, existingOptions);
    }

    try {
      const response = await lastValueFrom(
        clientListService.getClients({
          type: mergedConfig.type,
          [mergedConfig.searchField]: normalizedQuery,
          limit: mergedConfig.limit,
          is_deleted: mergedConfig.includeDeleted,
          include_cancelled: String(mergedConfig.includeCancelled),
          approval_status: 'approved,not_required',
        })
      );

      const clients: ClientApiResponse[] = response?.data?.clients || [];
      const clientOptions = this.formatClientOptions(clients);

      // const mergedOptions = this.mergeOptions(
      //   clientOptions,
      //   existingOptions,
      //   currentClientId
      // );

      const addressMap = this.buildAddressMap(clientOptions);
      const selectedClient = this.findSelectedClient(clientOptions, currentClientId);

      return {
        options: clientOptions,
        addresses: addressMap,
        selectedClient,
      };
    } catch (error) {
      console.error('[ClientTypeaheadUtils] Search error:', error);
      return {
        options: existingOptions,
        addresses: new Map(),
        selectedClient: this.findSelectedClient(existingOptions, currentClientId),
        error: error instanceof Error ? error.message : 'Search failed',
      };
    }
  }

  /**
   * Load a single client by ID and return formatted result
   */
  static async loadClientById(
    clientId: string | number,
    clientListService: ClientListService,
    includeSubsidiary: boolean = true
  ): Promise<ClientTypeaheadResult> {
    try {
      const response = await lastValueFrom(
        clientListService.getClientById(String(clientId), {
          include_subsidiary: includeSubsidiary ? 'true' : undefined
        })
      );

      const client: ClientApiResponse | undefined = 
        response?.data?.client || response?.data;

      if (!client) {
        return {
          options: [],
          addresses: new Map(),
          error: 'Client not found',
        };
      }

      const clientOption = this.formatClientOption(client);
      const addresses = this.extractClientAddresses(client);

      const addressMap = new Map<string, ClientAddresses>();
      addressMap.set(String(clientId), addresses);

      return {
        options: [clientOption],
        addresses: addressMap,
        selectedClient: clientOption,
      };
    } catch (error) {
      console.error('[ClientTypeaheadUtils] Load error:', error);
      return {
        options: [],
        addresses: new Map(),
        error: error instanceof Error ? error.message : 'Failed to load client',
      };
    }
  }

  // ==========================================================================
  // Private Helper Methods
  // ==========================================================================

  private static createEmptyResult(
    currentClientId: string | number | null | undefined,
    existingOptions: ClientOption[]
  ): ClientTypeaheadResult {
    return {
      options: [],
      addresses: new Map(),
      selectedClient: this.findSelectedClient(existingOptions, currentClientId),
    };
  }

  private static mergeOptions(
    newOptions: ClientOption[],
    existingOptions: ClientOption[],
    currentClientId?: string | number | null
  ): ClientOption[] {
    const preservedOptions = currentClientId
      ? existingOptions.filter(opt => this.areClientIdsEqual(opt.value, currentClientId))
      : [];

    const merged = [...preservedOptions, ...newOptions];
    const uniqueMap = new Map<string, ClientOption>();
    
    for (const opt of merged) {
      const key = String(opt.value);
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, opt);
      }
    }

    return Array.from(uniqueMap.values());
  }

  private static buildAddressMap(
    clientOptions: ClientOption[]
  ): Map<string, ClientAddresses> {
    const addressMap = new Map<string, ClientAddresses>();
    
    for (const client of clientOptions) {
      const addresses = this.extractClientAddresses(client as ClientApiResponse);
      if (addresses.shipping.length > 0 || addresses.billing.length > 0) {
        addressMap.set(String(client.value ?? client.id), addresses);
      }
    }

    return addressMap;
  }

  private static findSelectedClient(
    options: ClientOption[],
    clientId?: string | number | null
  ): ClientOption | undefined {
    if (!clientId) {
      return undefined;
    }
    return options.find(opt => this.areClientIdsEqual(opt.value, clientId));
  }
}
